import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { deleteRoleUserGroup } from 'common/services/api/RolesUserGroup/RoleUserGroup.thunk';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function RoleUserGroupDeleteDialogContent({
    title,
    itemId,
    editingRow
}: Readonly<DeleteDialogContentProps<{ userGroupName: string, userGroupId: string, roleName: string, roleId: string }>>) {
    const { userGroupName, userGroupId, roleName, roleId } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const params = useParams();
    const { tenantId } = params;
    const dispatch = useAppDispatch();
    const pageInfo = useAppSelector(selectPageInfo);

    const successHandler = () => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Role link has been deleted.', { variant: 'success' });
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        handleDeleteDialogError('Role link', enqueueSnackbar, extractStatusCode(error.message));
        setProcessing(false);
    };

    const roleUserGroupDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteRoleUserGroup({
                tenantId: tenantId ?? '',
                userGroupId: userGroupId ?? '',
                roleId: roleId ?? '',
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                })
                .finally(()=> {
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={roleUserGroupDeleteCallback} processing={processing ?? false} title={`${title} link`}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Role Name: <Typography component="span" color="text.primary" fontWeight="400">{roleName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User Group Name: <Typography component="span" color="text.primary" fontWeight="400">{userGroupName}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.tenantName}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
